import React, { useState } from 'react';

import {
    Box,
    CircularProgress,
    IconButton,
    InputAdornment,
    makeStyles,
    TextField,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import SearchIcon from '@material-ui/icons/Search';
import { format, parseISO } from 'date-fns';
import i18n from 'i18n-js';

import { images } from 'assets';
import { DateTimePicker } from 'components/shared';
import { theme } from 'config/theme';
import { DATE_FORMAT, formatReservationDate } from 'helpers/reservationHelper';
import { ReservationsTabFilter } from '../hooks';

const DATE_PICKER_TOGGLE_ID = 'date-time-picker';

const useStyles = makeStyles({
    root: {
        display: 'flex',
        alignItems: 'center',
        gap: 16,
        paddingBottom: 16,
        marginLeft: -2,
        marginTop: 18,
    },
    search: {
        width: 220,
    },
    date: {
        width: 224,
        '& input, & label': {
            cursor: 'pointer',
        },
    },
    icon: {
        color: theme.palette.text.disabled,
    },
});

interface ReservationsFilterBarProps {
    value: ReservationsTabFilter;
    onChange: (value: ReservationsTabFilter) => void;
    isLoading: boolean;
    summary?: React.ReactNode;
    pinnedDay?: string | null;
    onPinChange?: (day: string | null) => void;
}

export default function ReservationsFilterBar({
    value,
    onChange,
    isLoading,
    summary,
    pinnedDay,
    onPinChange,
}: ReservationsFilterBarProps) {
    const classes = useStyles();
    const [isPickerOpen, setIsPickerOpen] = useState(false);

    function submitDay(picked: Date, isPinOn?: boolean) {
        const day = format(picked, DATE_FORMAT);
        onChange({ ...value, day });
        onPinChange?.(isPinOn ? day : null);
    }

    return (
        <Box className={classes.root}>
            <TextField
                className={classes.search}
                variant="filled"
                label={i18n.t('reservations.filters.search')}
                placeholder={i18n.t('reservations.filters.searchPlaceholder')}
                InputLabelProps={{ shrink: true }}
                value={value.searchText}
                onChange={(event) => onChange({ ...value, searchText: event.target.value })}
                inputProps={{ 'data-testid': 'reservations-search' }}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            {isLoading && (
                                <CircularProgress
                                    size={16}
                                    data-testid="reservations-search-loading"
                                />
                            )}
                            <SearchIcon fontSize="small" className={classes.icon} />
                            {!!value.searchText && (
                                <IconButton
                                    size="small"
                                    aria-label={i18n.t('reservations.filters.clearSearch')}
                                    title={i18n.t('reservations.filters.clearSearch')}
                                    onClick={() => onChange({ ...value, searchText: '' })}
                                >
                                    <CloseIcon fontSize="small" />
                                </IconButton>
                            )}
                        </InputAdornment>
                    ),
                }}
            />
            <Box position="relative">
                <TextField
                    className={classes.date}
                    variant="filled"
                    label={i18n.t('reservations.filters.date')}
                    InputLabelProps={{ shrink: !!value.day || isPickerOpen }}
                    value={value.day ? formatReservationDate(value.day, 'dd.MM.yyyy') : ''}
                    onClick={() => setIsPickerOpen(true)}
                    inputProps={{
                        id: DATE_PICKER_TOGGLE_ID,
                        readOnly: true,
                        onKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault();
                                setIsPickerOpen(true);
                            }
                        },
                        'data-testid': 'reservations-date',
                    }}
                    InputProps={{
                        style: { cursor: 'pointer' },
                        endAdornment: (
                            <InputAdornment position="end">
                                {value.day && (
                                    <IconButton
                                        size="small"
                                        aria-label={i18n.t('reservations.filters.clearDate')}
                                        title={i18n.t('reservations.filters.clearDate')}
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            setIsPickerOpen(false);
                                            onChange({ ...value, day: null });
                                            onPinChange?.(null);
                                        }}
                                    >
                                        <CloseIcon fontSize="small" />
                                    </IconButton>
                                )}
                                <img src={images.icons.calendar} alt="" />
                            </InputAdornment>
                        ),
                    }}
                />
                <DateTimePicker
                    key={`${value.day ?? ''}|${pinnedDay ?? ''}`}
                    showButtons
                    alignLeft
                    hideTimeSelect
                    futureOnly
                    pin={
                        onPinChange
                            ? { label: i18n.t('reservations.pinDay'), isPinned: !!pinnedDay }
                            : undefined
                    }
                    date={value.day ? parseISO(value.day) : new Date()}
                    onSubmit={submitDay}
                    isOpen={isPickerOpen}
                    onClose={() => setIsPickerOpen(false)}
                />
            </Box>
            {summary}
        </Box>
    );
}
